/**
 * Legal page — /legale
 *
 * Consolidated legal document covering: Terms of Service, Privacy (GDPR),
 * Cookie policy, AI Act transparency, consent for biometric data, special
 * protections for departed persons under Italian Codice Privacy (D.lgs
 * 196/2003 art. 2-terdecies), right of withdrawal for EU digital purchases,
 * limitation of liability, and contact channels for rights requests.
 *
 * Text kept in Italian — the product's primary jurisdiction and audience.
 * Translations to EN/ES/PT/FR will be layered via the same locale system
 * once reviewed by counsel (P2). We deliberately did NOT route every
 * legal paragraph through t() yet, because:
 *   1) legal text must be updated atomically, not piecemeal per-language,
 *   2) unreviewed machine translations of legal text create more risk
 *      than they solve.
 *
 * IMPORTANT: this document is a solid professional baseline but is NOT
 * a substitute for attorney review. The banner at top makes that explicit.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";

// Single source of truth for the company's legal particulars. When you
// register the LLC / sole proprietorship, update ONLY these values — the
// whole page re-renders with the correct data.
const LEGAL_ENTITY = {
    brand: "Other Side",
    // TODO: replace with the registered business name once incorporated.
    legalName: "Other Side — nome legale da registrare",
    // TODO: legal domicile (via, cap, città, provincia, paese)
    address: "Indirizzo legale da aggiornare",
    vat: "P.IVA da registrare",
    emails: {
        general: "os.otherside.app@gmail.com",
        legal: "os.otherside.app@gmail.com",
        privacy: "os.otherside.app@gmail.com",
        dpo: "os.otherside.app@gmail.com",
    },
    lastUpdated: "21 aprile 2026",
};

export default function Legal() {
    const SECTIONS = useMemo(() => [
        { id: "servizio",   number: "01", heading: "Natura del servizio — cosa fa, cosa non fa" },
        { id: "eta",        number: "02", heading: "Età minima e idoneità" },
        { id: "account",    number: "03", heading: "Account, registrazione e sicurezza" },
        { id: "contenuti",  number: "04", heading: "Contenuti caricati dall'utente" },
        { id: "biometrici", number: "05", heading: "Dati biometrici — voce e volto" },
        { id: "defunti",    number: "06", heading: "Persone defunte" },
        { id: "viventi",    number: "07", heading: "Persone viventi e consenso" },
        { id: "acquisti",   number: "08", heading: "Acquisti, pagamenti e diritto di recesso" },
        { id: "privacy",    number: "09", heading: "Privacy, GDPR e trattamento dati" },
        { id: "cookie",     number: "10", heading: "Cookie e tecnologie simili" },
        { id: "responsabilita", number: "11", heading: "Limitazione di responsabilità e uso emotivo" },
        { id: "contatti",   number: "12", heading: "Contatti, reclami e foro competente" },
    ], []);

    return (
        <div className="grain min-h-screen" data-testid="legal-page">
            <Header minimal />

            <main className="max-w-4xl mx-auto px-6 md:px-12 pt-16 pb-32 animate-fade-in">
                <div className="flex items-center gap-4 mb-8">
                    <BrandMark size={40} />
                    <p className="label-eyebrow">Documento legale</p>
                </div>
                <h1 className="h-serif text-5xl md:text-6xl leading-[1.05] mb-6">
                    Termini, <span className="italic text-ink-gold">Privacy</span> e tutele.
                </h1>
                <p className="text-ink-textMid text-lg leading-[1.7] max-w-2xl mb-6">
                    Questo documento raccoglie in un unico luogo i Termini di Servizio,
                    l'Informativa Privacy (GDPR), la Cookie Policy e le nostre tutele
                    specifiche per l'uso dell'intelligenza artificiale nella ricostruzione
                    di presenze personali.
                </p>

                {/* Top-of-page attorney-review notice — legally prudent. */}
                <aside className="border-l-2 border-ink-gold pl-5 py-3 bg-ink-gold/5 mb-10" data-testid="legal-disclaimer">
                    <p className="text-sm text-ink-textMid italic leading-relaxed">
                        Ultimo aggiornamento: <strong className="text-ink-textHigh">{LEGAL_ENTITY.lastUpdated}</strong>.
                        Questo documento è vincolante per l'uso del servizio {LEGAL_ENTITY.brand}.
                        L'utilizzo dell'applicazione implica l'accettazione integrale dei termini qui descritti.
                    </p>
                </aside>

                {/* Sticky table of contents for quick navigation. */}
                <nav className="mb-16 border border-ink-border rounded-sm bg-ink-surface/60 p-6" aria-label="indice" data-testid="legal-toc">
                    <p className="label-eyebrow mb-4">Indice</p>
                    <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[0.95rem]">
                        {SECTIONS.map((s) => (
                            <li key={s.id}>
                                <a href={`#${s.id}`} className="text-ink-textMid hover:text-ink-gold transition-colors" data-testid={`toc-${s.id}`}>
                                    <span className="text-ink-gold/70 font-serif italic mr-2">{s.number}</span>
                                    {s.heading}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <ol className="space-y-20">
                    <Section id="servizio" number="01" heading="Natura del servizio — cosa fa, cosa non fa">
                        <p>
                            {LEGAL_ENTITY.brand} è un servizio di <strong className="text-ink-textHigh">ricostruzione espressiva
                            basata su intelligenza artificiale</strong>. Consente all'utente di creare una "presenza"
                            digitale a partire da ricordi, fotografie e registrazioni vocali da lui stesso forniti.
                            La presenza può conversare per iscritto, generare messaggi vocali e produrre brevi
                            video animati.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Il servizio non costituisce e non pretende di costituire
                            una comunicazione reale con la persona rappresentata.</strong> Tutti i contenuti generati sono
                            ricostruzioni statistiche prodotte da modelli di linguaggio (large language models)
                            e reti neurali per la sintesi audio-video. Ogni messaggio, ogni voce, ogni fotogramma
                            è frutto di un'elaborazione algoritmica.
                        </p>
                        <p>
                            Ai sensi del <strong className="text-ink-textHigh">Regolamento UE 2024/1689 (AI Act)</strong>, art. 50,
                            i contenuti generati tramite AI sono identificati come tali all'interno dell'applicazione
                            e sui media esportati (filigrana visibile). L'utente riconosce di aver compreso che si
                            tratta di contenuti sintetici e si impegna a non utilizzarli in contesti ove possano
                            essere scambiati per autentiche comunicazioni della persona rappresentata.
                        </p>
                        <p className="text-ink-textLow italic">
                            Il servizio non offre assistenza psicologica, consulenza medica, supporto in stato di
                            emergenza o servizi sanitari di alcun tipo. Non si sostituisce al lavoro di terapeuti,
                            psicologi, psichiatri, medici o autorità di pubblica sicurezza.
                        </p>
                    </Section>

                    <Section id="eta" number="02" heading="Età minima e idoneità">
                        <p>
                            Il servizio è riservato a persone che abbiano compiuto <strong className="text-ink-textHigh">diciotto (18)
                            anni</strong>. Non è consentito l'uso a minori di età, neppure con il consenso dei genitori o
                            dei tutori. La creazione dell'account richiede dichiarazione espressa di maggiore età.
                        </p>
                        <p>
                            L'utente dichiara inoltre di essere in condizioni psichiche tali da comprendere la natura
                            ricostruttiva dei contenuti e di poter gestire emotivamente l'esperienza proposta.
                            Si raccomanda cautela a chi sta affrontando un lutto recente: in tali casi, l'utilizzo
                            del servizio è opportuno solo con il supporto di un professionista qualificato.
                        </p>
                    </Section>

                    <Section id="account" number="03" heading="Account, registrazione e sicurezza">
                        <p>
                            Per utilizzare il servizio è necessario creare un account fornendo un indirizzo email
                            valido e una password. L'utente è responsabile della riservatezza delle credenziali
                            e di ogni attività compiuta tramite il proprio account.
                        </p>
                        <p>
                            Ci riserviamo il diritto di sospendere o disattivare account che violino questi Termini,
                            che vengano utilizzati per finalità illecite (tra cui creazione di contenuti diffamatori,
                            molestie, impersonificazione non consensuale, truffe), o che restino inattivi per oltre
                            ventiquattro (24) mesi consecutivi.
                        </p>
                        <p>
                            L'utente può eliminare il proprio account in qualsiasi momento scrivendo a <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a>.
                            La cancellazione comporta la rimozione definitiva delle presenze, delle conversazioni,
                            dei momenti generati e di tutti i dati personali associati entro trenta (30) giorni,
                            fatti salvi gli obblighi di conservazione previsti dalla legge (es. fatturazione).
                        </p>
                    </Section>

                    <Section id="contenuti" number="04" heading="Contenuti caricati dall'utente">
                        <p>
                            Caricando foto, registrazioni vocali, ricordi scritti, descrizioni di carattere o
                            frasi tipiche, l'utente dichiara di:
                        </p>
                        <ul className="list-none space-y-2 pl-2">
                            <li><span className="text-ink-gold mr-2">—</span> detenere legittimamente i contenuti caricati o avere il diritto di utilizzarli;</li>
                            <li><span className="text-ink-gold mr-2">—</span> non violare diritti d'autore, diritti di immagine, diritti di privacy di terzi;</li>
                            <li><span className="text-ink-gold mr-2">—</span> non caricare contenuti illeciti, violenti, pornografici, discriminatori o di incitamento all'odio;</li>
                            <li><span className="text-ink-gold mr-2">—</span> tenere indenne {LEGAL_ENTITY.brand} da qualsiasi pretesa avanzata da terzi in relazione ai contenuti caricati.</li>
                        </ul>
                        <p>
                            L'utente conserva la piena titolarità dei propri contenuti. Concede a {LEGAL_ENTITY.brand}
                            una licenza limitata, non esclusiva, gratuita, revocabile, esclusivamente finalizzata
                            all'erogazione del servizio (memorizzazione cifrata, elaborazione da parte dei modelli
                            AI, generazione dei contenuti derivati). La licenza si estingue automaticamente alla
                            cancellazione del contenuto o dell'account.
                        </p>
                    </Section>

                    <Section id="biometrici" number="05" heading="Dati biometrici — voce e volto">
                        <p>
                            <strong className="text-ink-textHigh">I dati vocali e i tratti del volto forniti dall'utente
                            costituiscono dati biometrici ai sensi dell'art. 9 GDPR</strong>, categoria speciale di dati
                            personali soggetta a tutele rafforzate.
                        </p>
                        <p>
                            Il loro trattamento avviene esclusivamente sulla base del <strong className="text-ink-textHigh">consenso
                            esplicito</strong> dell'interessato (art. 9, par. 2, lett. a GDPR), manifestato attraverso:
                        </p>
                        <ul className="list-none space-y-2 pl-2">
                            <li><span className="text-ink-gold mr-2">—</span> l'atto volontario di caricamento (foto, voce) all'interno dell'applicazione;</li>
                            <li><span className="text-ink-gold mr-2">—</span> la selezione esplicita, durante la creazione della presenza, di una delle due posture etiche previste ("persona defunta" / "persona vivente con consenso").</li>
                        </ul>
                        <p>
                            Tali dati non vengono ceduti a terzi a fini commerciali, non alimentano modelli AI di
                            terze parti al di fuori dei servizi strettamente necessari per il funzionamento
                            (elaborazione voce tramite Whisper / ElevenLabs; generazione video tramite fal.ai),
                            non sono utilizzati per finalità di marketing, targeting, profilazione o ricerca.
                        </p>
                        <p>
                            Il consenso può essere revocato in qualsiasi momento eliminando la presenza o cancellando
                            l'account. La revoca non pregiudica la liceità del trattamento antecedente.
                        </p>
                    </Section>

                    <Section id="defunti" number="06" heading="Persone defunte">
                        <p>
                            Il Regolamento UE 2016/679 (GDPR) non si applica ai dati delle persone decedute. Tuttavia,
                            in Italia, <strong className="text-ink-textHigh">l'art. 2-terdecies del D.lgs 196/2003 (Codice Privacy)</strong>
                            riconosce a chi ha un interesse proprio, o agisce a tutela dell'interessato o per ragioni
                            familiari meritevoli di protezione, la facoltà di esercitare i diritti di accesso,
                            rettifica, cancellazione e opposizione relativamente ai dati del defunto.
                        </p>
                        <p>
                            Quando l'utente crea una presenza dichiarandola come "persona defunta", si assume la
                            piena responsabilità morale e giuridica della ricostruzione. Familiari, eredi o soggetti
                            aventi titolo che si ritengano lesi dalla ricostruzione possono richiederne la rimozione
                            scrivendo a <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a> con
                            documentazione che attesti il rapporto di parentela o il titolo legittimante.
                        </p>
                        <p>
                            La ricostruzione non può essere utilizzata per finalità diffamatorie, per creare
                            dichiarazioni attribuite al defunto in contesti pubblici, per scopi politici, per
                            truffe identitarie o per qualsiasi uso lesivo della memoria della persona rappresentata.
                        </p>
                    </Section>

                    <Section id="viventi" number="07" heading="Persone viventi e consenso">
                        <p>
                            Quando l'utente crea una presenza basata su una persona vivente, <strong className="text-ink-textHigh">dichiara
                            e garantisce di aver ottenuto il consenso espresso</strong> della stessa alla creazione
                            della ricostruzione digitale. La dichiarazione avviene tramite spunta esplicita durante
                            il processo di creazione.
                        </p>
                        <p>
                            La persona vivente oggetto di ricostruzione può, in qualsiasi momento, chiedere la
                            cessazione del trattamento e la rimozione integrale dei propri dati contattando
                            <a className="text-ink-gold underline underline-offset-4 ml-1" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a>.
                            A tutela dell'interessato, {LEGAL_ENTITY.brand} dà corso alla richiesta entro
                            quarantotto (48) ore dalla ricezione, anche in assenza di formale verifica d'identità,
                            salvo conferma successiva.
                        </p>
                        <p>
                            La creazione di presenze di persone viventi a loro insaputa o contro la loro volontà
                            costituisce violazione dei presenti Termini e può configurare illeciti civili e penali
                            (trattamento illecito di dati, sostituzione di persona, diffamazione). {LEGAL_ENTITY.brand}
                            collabora con le autorità competenti e si riserva ogni azione contro gli utenti
                            responsabili, compresa la cancellazione immediata dell'account.
                        </p>
                    </Section>

                    <Section id="acquisti" number="08" heading="Acquisti, pagamenti e diritto di recesso">
                        <p>
                            Alcune funzionalità del servizio (generazione di "momenti" video con voce, piani di
                            abbonamento, ricostruzione avanzata) sono a pagamento. I prezzi sono indicati in euro,
                            IVA inclusa ove applicabile, sulla pagina di checkout. Il pagamento avviene tramite
                            fornitore certificato (<strong className="text-ink-textHigh">Stripe</strong>); {LEGAL_ENTITY.brand}
                            non memorizza i dati della carta di pagamento.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Diritto di recesso.</strong> Ai sensi dell'art. 59, lett. m) del
                            Codice del Consumo (D.lgs 206/2005), il diritto di recesso di quattordici (14) giorni
                            normalmente previsto per i contratti a distanza <strong className="text-ink-textHigh">non si applica</strong> alla
                            fornitura di contenuti digitali generati su richiesta specifica dell'utente quando
                            l'esecuzione sia iniziata con il suo espresso consenso e con la sua accettazione della
                            perdita del diritto di recesso. Confermando l'acquisto di un "momento" o di un contenuto
                            generato, l'utente dichiara di acconsentire all'esecuzione immediata e di rinunciare
                            espressamente al diritto di recesso.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Rimborsi.</strong> Qualora la generazione del contenuto acquistato
                            non vada a buon fine per cause tecniche imputabili a {LEGAL_ENTITY.brand} e non rimediabili
                            entro quarantotto (48) ore, l'utente ha diritto al rimborso integrale, da richiedere
                            a <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.general}`}>{LEGAL_ENTITY.emails.general}</a>.
                            Nessun rimborso è dovuto per scelte estetiche, linguistiche o emotive dell'utente
                            rispetto al contenuto generato, che per sua natura è il prodotto di un modello statistico.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Abbonamenti ricorrenti.</strong> Gli abbonamenti si rinnovano
                            automaticamente sino a cancellazione, eseguibile in ogni momento dalla sezione
                            Impostazioni / Piano. La cancellazione diventa efficace al termine del periodo
                            già pagato; non sono previsti rimborsi parziali per periodi non goduti.
                        </p>
                    </Section>

                    <Section id="privacy" number="09" heading="Privacy, GDPR e trattamento dati">
                        <p>
                            <strong className="text-ink-textHigh">Titolare del trattamento:</strong> {LEGAL_ENTITY.legalName},
                            con sede in {LEGAL_ENTITY.address} ({LEGAL_ENTITY.vat}). Contatto privacy:
                            <a className="text-ink-gold underline underline-offset-4 ml-1" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a>.
                            Responsabile della Protezione dei Dati (DPO): <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.dpo}`}>{LEGAL_ENTITY.emails.dpo}</a>.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Dati trattati:</strong> email, password (cifrata con bcrypt),
                            nome visualizzato, lingua preferita, dati biometrici (voce, volto), ricordi e descrizioni
                            caricati, cronologia conversazioni, metadati d'uso (log, indirizzi IP, user agent),
                            dati di pagamento (gestiti da Stripe).
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Finalità e basi giuridiche:</strong>
                        </p>
                        <ul className="list-none space-y-2 pl-2">
                            <li><span className="text-ink-gold mr-2">—</span> erogazione del servizio — art. 6, par. 1, lett. b) GDPR (esecuzione contratto);</li>
                            <li><span className="text-ink-gold mr-2">—</span> trattamento dati biometrici — art. 9, par. 2, lett. a) GDPR (consenso esplicito);</li>
                            <li><span className="text-ink-gold mr-2">—</span> adempimenti fiscali e contabili — art. 6, par. 1, lett. c) GDPR (obbligo di legge);</li>
                            <li><span className="text-ink-gold mr-2">—</span> sicurezza dell'applicazione e prevenzione frodi — art. 6, par. 1, lett. f) GDPR (legittimo interesse).</li>
                        </ul>
                        <p>
                            <strong className="text-ink-textHigh">Fornitori (sub-responsabili):</strong> i dati sono trattati,
                            per le sole finalità di erogazione del servizio, dai seguenti fornitori tecnici:
                            Anthropic (generazione testo), OpenAI Whisper (trascrizione vocale), ElevenLabs
                            (sintesi vocale), fal.ai / Kling (generazione video), Stripe (pagamenti),
                            Emergent (hosting e object storage), MongoDB (database). Tutti i fornitori operano
                            in regime contrattuale conforme al GDPR e, ove applicabile, con Clausole Contrattuali
                            Standard per trasferimenti extra-UE.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Conservazione:</strong> i dati sono conservati per tutta la durata
                            dell'account e per i trenta (30) giorni successivi alla cancellazione, salvo obblighi
                            di legge più lunghi (es. 10 anni per documenti fiscali). I log tecnici sono conservati
                            al massimo per novanta (90) giorni.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Diritti dell'interessato:</strong> l'utente può esercitare i
                            diritti di accesso (art. 15), rettifica (art. 16), cancellazione (art. 17), limitazione
                            (art. 18), portabilità (art. 20), opposizione (art. 21) e revoca del consenso in qualsiasi
                            momento, scrivendo a <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a>.
                            La risposta viene fornita entro trenta (30) giorni. È altresì riconosciuto il diritto
                            di proporre reclamo al <strong className="text-ink-textHigh">Garante per la protezione dei dati personali</strong>
                            (<a className="text-ink-gold underline underline-offset-4" href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer">garanteprivacy.it</a>).
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Trasferimenti extra-UE:</strong> alcuni dei fornitori sopra citati
                            (es. Anthropic, OpenAI, Stripe) hanno sede negli Stati Uniti. I trasferimenti avvengono
                            sulla base delle Clausole Contrattuali Standard approvate dalla Commissione Europea
                            (decisione 2021/914) e, ove applicabile, del framework EU-US Data Privacy Framework.
                        </p>
                    </Section>

                    <Section id="cookie" number="10" heading="Cookie e tecnologie simili">
                        <p>
                            L'applicazione utilizza esclusivamente <strong className="text-ink-textHigh">cookie tecnici</strong> e tecnologie
                            equivalenti (localStorage, sessionStorage) strettamente necessari al funzionamento del
                            servizio. Tra questi: mantenimento della sessione autenticata, memorizzazione della
                            lingua preferita, memorizzazione delle preferenze audio.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Non utilizziamo cookie di profilazione, cookie pubblicitari, pixel
                            di tracciamento o strumenti di analisi comportamentale di terze parti.</strong> Il consenso
                            esplicito non è quindi richiesto per i cookie tecnici (art. 122 D.lgs 196/2003, Direttiva
                            ePrivacy).
                        </p>
                        <p>
                            Il sito può includere cookie tecnici del fornitore di pagamento (Stripe) limitatamente
                            alla pagina di checkout e per finalità di prevenzione frodi. Tali cookie sono disciplinati
                            dalla <a className="text-ink-gold underline underline-offset-4" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">privacy policy di Stripe</a>.
                        </p>
                        <p>
                            L'utente può disattivare i cookie dal proprio browser; alcune funzionalità potrebbero
                            di conseguenza risultare non disponibili.
                        </p>
                    </Section>

                    <Section id="responsabilita" number="11" heading="Limitazione di responsabilità e uso emotivo">
                        <p>
                            {LEGAL_ENTITY.brand} fornisce il servizio <strong className="text-ink-textHigh">"così com'è" e "secondo
                            disponibilità"</strong>. Pur adottando elevati standard di qualità, non garantisce l'esattezza,
                            la verosimiglianza, la pertinenza emotiva o la fedeltà dei contenuti generati rispetto
                            alla persona rappresentata: si tratta, per definizione, di ricostruzioni probabilistiche
                            basate sulle informazioni fornite dall'utente.
                        </p>
                        <p>
                            Entro i limiti massimi consentiti dalla legge, {LEGAL_ENTITY.brand} non risponde di:
                        </p>
                        <ul className="list-none space-y-2 pl-2">
                            <li><span className="text-ink-gold mr-2">—</span> danni morali o emotivi derivanti dall'uso del servizio;</li>
                            <li><span className="text-ink-gold mr-2">—</span> fraintendimenti o disagi causati dalla natura ricostruttiva dei contenuti;</li>
                            <li><span className="text-ink-gold mr-2">—</span> mancate generazioni dovute a indisponibilità dei fornitori tecnici a monte;</li>
                            <li><span className="text-ink-gold mr-2">—</span> contenuti generati che, pur tecnicamente leciti, possano risultare inopportuni o dolorosi per l'utente.</li>
                        </ul>
                        <p>
                            <strong className="text-ink-textHigh">In caso di sofferenza psichica</strong>, l'utente è invitato a
                            interrompere l'uso del servizio e a rivolgersi a un professionista sanitario qualificato.
                            In situazioni di emergenza (ideazione suicidaria, crisi acuta) si contattino senza
                            indugio i servizi sanitari di emergenza: in Italia <strong className="text-ink-textHigh">Telefono Amico (02 2327 2327)</strong>,
                            <strong className="text-ink-textHigh"> Telefono Azzurro (19696)</strong>, <strong className="text-ink-textHigh">Numero Unico Emergenze 112</strong>.
                        </p>
                        <p>
                            Nessuna clausola di limitazione di responsabilità esclude o limita la responsabilità
                            per dolo, colpa grave, lesioni personali o altri casi in cui la limitazione non sia
                            consentita dalla legge applicabile.
                        </p>
                    </Section>

                    <Section id="contatti" number="12" heading="Contatti, reclami e foro competente">
                        <p>
                            Per qualsiasi richiesta, reclamo o chiarimento si possono utilizzare i seguenti canali:
                        </p>
                        <ul className="list-none space-y-2 pl-2">
                            <li><span className="text-ink-gold mr-2">—</span> Questioni generali: <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.general}`}>{LEGAL_ENTITY.emails.general}</a></li>
                            <li><span className="text-ink-gold mr-2">—</span> Questioni legali: <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.legal}`}>{LEGAL_ENTITY.emails.legal}</a></li>
                            <li><span className="text-ink-gold mr-2">—</span> Richieste privacy e diritti dell'interessato: <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.privacy}`}>{LEGAL_ENTITY.emails.privacy}</a></li>
                            <li><span className="text-ink-gold mr-2">—</span> Data Protection Officer: <a className="text-ink-gold underline underline-offset-4" href={`mailto:${LEGAL_ENTITY.emails.dpo}`}>{LEGAL_ENTITY.emails.dpo}</a></li>
                        </ul>
                        <p>
                            <strong className="text-ink-textHigh">Modifiche ai presenti Termini.</strong> {LEGAL_ENTITY.brand} si
                            riserva il diritto di aggiornare questo documento. Gli utenti sono informati delle
                            modifiche sostanziali via email o attraverso comunicazione in-app, con preavviso di
                            almeno trenta (30) giorni prima dell'entrata in vigore.
                        </p>
                        <p>
                            <strong className="text-ink-textHigh">Legge applicabile e foro.</strong> Salvo diverse norme inderogabili
                            di tutela del consumatore, i presenti Termini sono disciplinati dalla <strong className="text-ink-textHigh">legge
                            italiana</strong>. Per ogni controversia è competente il Foro del domicilio del consumatore
                            se residente in Italia; in tutti gli altri casi, il Foro della sede legale del Titolare.
                            I consumatori residenti nell'Unione Europea possono inoltre accedere alla piattaforma
                            <a className="text-ink-gold underline underline-offset-4 ml-1" href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer">ODR della Commissione Europea</a>
                            per la risoluzione extragiudiziale delle controversie.
                        </p>
                    </Section>
                </ol>

                <div className="border-t border-ink-border pt-10 mt-20 text-sm text-ink-textMid flex flex-wrap items-center gap-4" data-testid="legal-footer">
                    <span>Ultimo aggiornamento: <strong className="text-ink-textHigh">{LEGAL_ENTITY.lastUpdated}</strong></span>
                    <span className="opacity-40">·</span>
                    <Link to="/" className="hover:text-ink-textHigh">Home</Link>
                    <span className="opacity-40">·</span>
                    <a href={`mailto:${LEGAL_ENTITY.emails.legal}`} className="hover:text-ink-textHigh">{LEGAL_ENTITY.emails.legal}</a>
                </div>
            </main>
        </div>
    );
}

function Section({ id, number, heading, children }) {
    return (
        <li id={id} className="scroll-mt-24 border-t border-ink-border pt-10" data-testid={`legal-section-${id}`}>
            <div className="flex items-baseline gap-4 mb-5">
                <span className="font-serif italic text-ink-gold text-xl">{number}</span>
                <h2 className="h-serif text-3xl md:text-[2rem] text-ink-textHigh leading-snug">{heading}</h2>
            </div>
            <div className="space-y-5 font-serif text-[1.12rem] text-ink-textMid leading-[1.8] max-w-[66ch]">
                {children}
            </div>
        </li>
    );
}
